import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectParticipantAttendedModules } from '../../../../store/courseSelectors';
import { loadHealthEventTypes } from '../../../../store/directusService';
import {
    clearHealthIncidents,
    deleteHealthIncident,
    loadHealthIncidents
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    HEALTH_EVENT_TYPES_ERROR,
    HEALTH_INCIDENTS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import EditHealthIncident from './EditHealthIncident';
import HealthIncidentRow from './HealthIncidentRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    { id: 'eventTypes', numeric: false, label: 'Event Types' },
    { id: 'dateReportedToNhse', numeric: false, label: 'Date Reported to NHSE' },
    {
        id: 'programmeDeIntensifiedDate',
        numeric: false,
        label: 'Programme De-Intensified Date'
    },
    {
        id: 'programmeReIntensifiedDate',
        numeric: false,
        label: 'Programme Re-Intensified Date'
    }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const HealthIncident = ({
    participant,
    isEditDisabled = true,
    isDeleteDisabled = true,
    isCreateDisabled = true,
    linkedModuleId
}) => {
    const dispatch = useDispatch();
    const healthIncidentRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [modules, setModules] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const healthEventTypes = useSelector(
        (state) => state.entities.directusService.healthEventTypes
    );
    const healthIncidents = useSelector(
        (state) => state.entities.participantService.healthIncidents
    );
    const healthIncidentsMetaData = useSelector(
        (state) => state.entities.participantService.healthIncidentsMetaData
    );
    const attendedModules = useSelector(selectParticipantAttendedModules);

    // USEEFFECTS
    useEffect(() => {
        if (healthEventTypes?.length < 1) dispatch(loadHealthEventTypes());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        participant?.id && dispatch(loadHealthIncidents(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (healthEventTypes?.length < 1 || !healthIncidentsMetaData.loaded) return;

        let modules = [];
        if (!healthIncidents?.length) {
            rows.length > 0 && setRows([]);
            if (attendedModules?.length) modules = [...attendedModules];
            setLoadingContentMessage(HEALTH_INCIDENTS_ERROR);
        } else {
            const rows = healthIncidents
                .filter((el) => !el.inactive)
                .map((el) => ({
                    ...el,
                    eventTypes:
                        el.healthEventTypeIds
                            ?.map((entry) => healthEventTypes.find((obj) => entry === obj.id)?.name)
                            .filter(Boolean)
                            .sort((a, b) => a.localeCompare(b)) || [],
                    linkedModule: el.linkedModuleId
                        ? attendedModules?.find((entry) => entry.id === el.linkedModuleId)?.title
                        : ''
                }));
            setRows(rows);

            if (attendedModules?.length) {
                modules = attendedModules.filter(
                    (el) => !healthIncidents.find((entry) => entry.linkedModuleId === el.id)
                );
            }
        }
        setModules(modules.map((el) => ({ ...el, name: el.title })) || []);
    }, [healthIncidents, healthIncidentsMetaData, healthEventTypes, attendedModules]);

    // FNs
    const loadingError = () => {
        if (healthEventTypes?.length < 1) return HEALTH_EVENT_TYPES_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <HealthIncidentRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                toggleDropdown={setOpenId}
                openId={openId}
            />
        ));

    const cleanUp = () => {
        dispatch(clearHealthIncidents());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteHealthIncident(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(healthIncidentRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    return (
        <div
            className={local.wellBeingSection}
            data-testid="health_incident"
            id="health_incident"
            ref={healthIncidentRef}>
            <h2 className={app.sectionHeading}>Health Incident</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addHealthIncidentButton"
                        type="button"
                        disabled={isCreateDisabled}
                        content="Add New Health Incident"
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditHealthIncident
                        participant={participant}
                        onClose={onClose}
                        isDisabled={
                            (formType === 'edit' && isEditDisabled) ||
                            (formType === 'create' && isCreateDisabled)
                        }
                        attendedModules={modules}
                        linkedModuleId={linkedModuleId || ''}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

HealthIncident.propTypes = {
    participant: PropTypes.object.isRequired,
    isEditDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isCreateDisabled: PropTypes.bool,
    linkedModuleId: PropTypes.string
};

export default HealthIncident;
